import { useEffect, useRef } from "@modica-library/react19";

/**
 * Mounts a web2text custom element (fed-web-to-text build, self-hosted in
 * public/w2t) safely from React.
 *
 * Why not plain JSX: the w2t elements call `this.appendChild(...)` in their
 * constructor, which the `document.createElement` path React uses rejects
 * ("the result must not have children"). Creating the element in an inert
 * document (no custom-element registry, so no constructor runs) and adopting
 * it into the page defers the upgrade to connection time — the same code path
 * as Omni's server-rendered pages — with no HTML strings involved.
 */
type W2TTag = "web2text-send-message" | "web2text-conversations";

export function W2TElement({ tag, attrs }: { tag: W2TTag; attrs: Record<string, string> }) {
  const hostRef = useRef<HTMLDivElement>(null);
  // The demo attrs are static config; snapshot the first render's values so
  // the element isn't torn down on incidental re-renders.
  const attrsRef = useRef(attrs);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const inertDoc = document.implementation.createHTMLDocument("");
    const el = inertDoc.createElement(tag);
    for (const [name, value] of Object.entries(attrsRef.current)) {
      el.setAttribute(name, value);
    }
    host.appendChild(document.adoptNode(el));

    return () => {
      el.remove();
    };
  }, [tag]);

  return <div ref={hostRef} />;
}
